// src/Pages/UKK/UKKArtists.jsx
import React, { useLayoutEffect, useRef, useState, useEffect } from "react";
import { Helmet } from "@dr.pogodin/react-helmet";
import { motion } from "../../lib/motionSafe.js"; // polku suhteessa komponenttiin
import { HelpCircle, ChevronDown } from "lucide-react";
import { useTheme } from "../../context/ThemeContext";
import VideoHero from "../../components/VideoHero/VideoHero.jsx";
import ukkhero from "../../assets/performer-ukk.mp4";
import poster from "../../assets/ukk-hero-poster.webp";

/* Vakaa, saavutettava akordeoni – sama malli kuin UKK.jsx:ssä */
function FAQItem({ i, title, children, isOpen, onToggle }) {
  const { theme } = useTheme();
  const panelId = `faq-artists-panel-${i}`;
  const btnId = `faq-artists-button-${i}`;

  const contentRef = useRef(null);
  const [contentH, setContentH] = useState(0);

  useLayoutEffect(() => {
    if (contentRef.current) setContentH(contentRef.current.scrollHeight);
  }, [children, isOpen, title]);

  useEffect(() => {
    const onResize = () => {
      if (contentRef.current) setContentH(contentRef.current.scrollHeight);
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const headerBase =
    theme === "dark"
      ? "bg-[#1a1630]/70 hover:bg-[#221b44]/80"
      : "bg-lemon/70 hover:bg-lemon/80";

  const containerRing = isOpen ? "ring-primary/40" : "ring-white/10";
  const containerBg = theme === "dark" ? "bg-white/5" : "bg-white/50";
  const bodyText = theme === "dark" ? "text-white/85" : "text-black/85";
  const titleText = theme === "dark" ? "text-white/90" : "text-black/90";

  return (
    <div className={`rounded-xl overflow-hidden backdrop-blur-md ring-1 ${containerRing} ${containerBg}`}>
      {/* Otsikkorivi */}
      <button
        id={btnId}
        aria-controls={panelId}
        aria-expanded={isOpen}
        onClick={onToggle}
        className={`group w-full flex items-center justify-between gap-4 px-4 sm:px-5 py-4 text-left ${headerBase}`}
      >
        {/* MUUTOS: lisätty min-w-0 vanhemmalle ja rivittymisluokat otsikolle */}
        <span className="flex items-center gap-3 min-w-0">
          {/* min-w-0 antaa lapsen kutistua flexissä */}
          <HelpCircle className="h-5 w-5 dark:text-primary drop-shadow" />
          <span
            className={
              `flex-1 min-w-0 break-words whitespace-normal ` + // ← varmistaa rivittymisen
              `font-customFont2 dark:text-white/80 text-black/80 ` +
              `text-lg xxxsm:text-xs xxsm:text-xs s24:text-xs xsm:text-xs sm:text-sm md:text-sm mdd:text-sm ${titleText}`
            }
          >
            {title}
          </span>
        </span>

        <motion.span
          animate={{ rotate: isOpen ? 180 : 0 }}
          transition={{ type: "spring", stiffness: 260, damping: 22 }}
          className="inline-flex shrink-0 rounded-md ring-1 ring-white/10 bg-white/10 p-1"
        >
          <ChevronDown className="h-4 w-4" />
        </motion.span>
      </button>

      {/* Sisältö – animoidaan numeeriseen korkeuteen (ei height:auto) */}
      <motion.div
        id={panelId}
        role="region"
        aria-labelledby={btnId}
        initial={false}
        animate={{ height: isOpen ? contentH : 0, opacity: isOpen ? 1 : 0 }}
        transition={{ duration: 0.28, ease: "easeInOut" }}
        style={{ overflow: "hidden" }}
      >
        <div ref={contentRef} className="px-4 sm:px-5 pb-4 pt-1">
          <div className={`font-customFont1 text-lg xxxsm:text-xs xxsm:text-xs s24:text-xs xsm:text-xs sm:text-sm md:text-sm mdd:text-sm leading-relaxed ${bodyText}`}>
            {children}
          </div>
        </div>
      </motion.div>
    </div>
  );
}

/* JSON-LD: FAQPage esiintyjille */
const faqEsiintyjatJsonLd = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "mainEntity": [
    {
      "@type": "Question",
      "name": "Miten esiintyjäksi pääsee mukaan?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Rekisteröidy Esiintyjille-osion kautta, vahvista sähköpostisi ja luo hallintapaneelissa ensimmäinen esiintyjäkorttisi. Kun profiili on täytetty ja hyväksytty, se näkyy haussa ja voit vastaanottaa tarjouspyyntöjä."
      }
    },
    {
      "@type": "Question",
      "name": "Maksaako liittyminen?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Esiintyjäksi liittyminen Buukkeriin on maksutonta. Voit luoda tilin ja ensimmäisen esiintyjäkortin ilman liittymismaksua; mahdolliset lisäkortit ja lisäpalvelut ovat erikseen hinnoiteltuja."
      }
    },
    {
      "@type": "Question",
      "name": "Mitä tarvitaan, jotta profiilin voi julkaista?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Profiilin voi lähettää tarkistettavaksi, kun perusprofiilitiedot, vähintään yksi galleria-kuva, vähintään yksi video tai ääniraita sekä vähintään yksi avoin viikonloppu ja yksi setti ja hinta vähintään yhdelle maakunnalle on lisätty."
      }
    },
    {
      "@type": "Question",
      "name": "Miten hinnoittelen esiintymiseni?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Hinnoittelet itse esiintymisesi kokonaissummana. Asiakkaalle näytetään tämä summa, johon lisätään automaattisesti matkakulut 0,59 €/km. Huomioi matkakulut hinnoittelussasi."
      }
    },
    {
      "@type": "Question",
      "name": "Miten päivitän saatavuuden?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Saatavuus päivitetään hallintapaneelin esiintymiskalenterissa. Merkitse vihreäksi ne päivät jolloin haluat ottaa tarjouspyyntöjä vastaan ja säädä tarvittaessa puskuriaika, joka vaikuttaa näkyvyyteesi hakutuloksissa."
      }
    }
  ]
};

export default function UKKArtists() {
  const { theme } = useTheme();
  const [openIndex, setOpenIndex] = useState(0);
  const toggle = (idx) => setOpenIndex((prev) => (prev === idx ? -1 : idx)); // ✅ käytössä

  const heroOverlay =
    theme === "dark"
      ? "from-black/60 via-black/35 to-black/60"
      : "from-white/20 via-white/5 to-white/0";

  // Esiintyjille suunnattu sisältö
  const faqs = [
    {
      title: "Miten esiintyjäksi pääsee mukaan?",
      content: (
        <>
          Rekisteröidy ylävalikosta kohdasta <strong>Esiintyjille</strong> →{" "}
          <strong>Luo tili / Rekisteröidy</strong>.<br />
          Vahvista sähköpostisi, hyväksy käyttöehdot ja luo hallintapaneelissa ensimmäinen{" "}
          <strong>esiintyjäkorttisi</strong>. Täytä profiili huolellisesti ja lähetä se tarkistettavaksi.<br />
          Hyväksynnän jälkeen profiilisi näkyy haussa ja voit vastaanottaa tarjouspyyntöjä.
        </>
      ),
    },

    {
      title: "Jos rekisteröidyn esiintyjäksi, minkälaiset keikkailukamat minulla pitää olla?",
      content: (
        <>
          <p>
            Buukkeri on tarkoitettu jo keikkaa tehneille esiintyjille. Esiintyjällä tulee olla
            perusäänentoisto kunnossa ja valmius esiintyä ilman talon tekniikkaa.
          </p>
        </>
      ),
    },

    {
      title: "Maksaako liittyminen?",
      content: (
        <>
          Ei. Liittyminen Buukkeriin on <strong>maksutonta</strong>.
        </>
      ),
    },

    {
      title: "Mitä tarvitaan, jotta profiilin voi julkaista?",
      content: (
        <>
          Profiili voidaan lähettää tarkistettavaksi, kun seuraavat tiedot on lisätty:
          <ul className="list-disc list-inside mt-2 space-y-1">
            <li>Profiilikuva, esiintyjätyyppi, kotipaikka, sähköposti, puhelin ja kuvaus</li>
            <li>Vähintään yksi kuva galleriaan</li>
            <li>Vähintään yksi video tai ääniraita näytteeksi</li>
            <li>
              Kalenteriin <strong>vähintään yksi perjantai + lauantai -pari avoinna (vihreänä)</strong> seuraavan 30 päivän
              sisällä.
            </li>
            <li>Vähintään yksi setti ja hinta vähintään yhdelle maakunnalle</li>
          </ul>
          <p className="mt-2 text-sm opacity-80">
            Kun kaikki kohdat on täytetty, lähetä profiili tarkistukseen ja julkaistaan hyväksynnän jälkeen.
          </p>
        </>
      ),
    },

    {
      title: "Mitä tilin luonti vaatii?",
      content: (
        <>
          <ul className="list-disc list-inside mt-2 space-y-1">
            <li>Tilin luontiin riittää nimi, sähköposti ja salasana.</li>
            <li>Puhelinnumero kannattaa lisätä myös hallintapaneelissa.</li>
          </ul>
        </>
      ),
    },

    {
      title: "Miten hinnoittelen esiintymiseni?",
      content: (
        <>
          Hinnoittelet itse oman esiintymisesi <strong>kokonaissummana</strong>.<br />
          Asiakkaalle näytetään tämä summa, johon lisätään automaattisesti{" "}
          <strong>matkakulut 0,59&nbsp;€/km</strong>.
          <ul className="list-disc list-inside mt-2 space-y-1">
            <li>Huomioi matkakulut hinnoittelussasi.</li>
          </ul>
        </>
      ),
    },

    {
      title: "Miten päivitän saatavuuden?",
      content: (
        <>
          Saatavuus päivitetään <strong>hallintapaneelin esiintymiskalenterissa</strong>.<br />
          Avaa kalenteri ja merkitse sopivat päivät <strong>vihreiksi</strong>, kun haluat vastaanottaa
          tarjouspyyntöjä. <em>Vihreä</em> tarkoittaa avointa päivää, <em>harmaa</em> että et ole saatavilla.
          <ul className="list-disc list-inside mt-2 space-y-1">
            <li>Voit asettaa <strong>puskuriajan</strong>, joka kertoo kuinka pian olet valmis lähtemään keikalle.</li>
            <li><strong>Avoimet päivät</strong> ja <strong>puskuri</strong> vaikuttavat näkyvyyteesi hakutuloksissa.</li>
          </ul>
        </>
      ),
    },

    {
      title: "Miksi profiilisivullani lukee “Huomio! Tämä profiili ei ole juuri nyt näkyvissä”?",
      content: (
        <>
          <p className="mb-2">
            Ilmoitus tulee, kun profiili ei täytä näkyvyyskriteerejä. Profiili pysyy näkyvänä, kun
            <strong> jompikumpi</strong> seuraavista toteutuu seuraavan 30 päivän aikana:
          </p>

          <ul className="list-disc pl-5 space-y-1">
            <li>
              Kalenterissa on vähintään yksi viikonloppu, jossa <strong>perjantai</strong> ja{" "}
              <strong>lauantai</strong> ovat{" "}
              <span className="text-green-600 font-semibold">vihreällä</span> (buukattavissa).
            </li>
            <li>
              Kalenterissa on vähintään yksi päivä, joka on{" "}
              <span className="text-yellow-600 font-semibold">keltaisella</span> (tarjous vireillä) tai{" "}
              <span className="text-red-600 font-semibold">punaisella</span> (buukattu).
            </li>
          </ul>

          <p className="mt-3 font-semibold">Mikä ei riitä?</p>
          <ul className="list-disc pl-5 space-y-1">
            <li>Yksittäinen vihreä päivä ilman perjantai+lauantai -paria.</li>
            <li>
              <span className="text-gray-600 font-semibold">Harmaat</span> päivät (ei buukattavissa).
            </li>
          </ul>

          <p className="mt-3 font-semibold">Miten korjaan tilanteen?</p>
          <ul className="list-disc pl-5 space-y-1">
            <li>Avaa hallintapaneelin <strong>kalenteri</strong>.</li>
            <li>Merkitse vähintään yksi <strong>perjantai + lauantai</strong> vihreäksi seuraavan 30 päivän sisällä,</li>
            <li>
              tai pidä kalenterissa vähintään yksi{" "}
              <span className="text-yellow-600 font-semibold">keltainen</span> tai{" "}
              <span className="text-red-600 font-semibold">punainen</span> päivä.
            </li>
            <li>Tarkista <strong>puskuriaika</strong>, ettei se piilota lähiviikon päiviä.</li>
          </ul>

          <p className="mt-3 text-sm opacity-80">
            Huom: asiakkaan tekemä tarjous vanhenee tunnissa.
          </p>
        </>
      ),
    },

    {
      title: "Käytöskoodi keikoilla",
      content: (
        <>
          Buukkerin esiintyjiltä odotetaan ammattimaista ja vastuullista toimintaa.
          <ul className="list-disc pl-5 mt-2 space-y-1">
            <li><strong>Yhdenvertaisuus:</strong> nollatoleranssi syrjinnälle, rasismille ja häirinnälle.</li>
            <li><strong>Ammattimaisuus:</strong> asiallinen käytös koko tapahtuman ajan, ei esiintymistä päihtyneenä.</li>
            <li><strong>Rehellisyys ja avoimuus:</strong> toimitaan sovittujen ehtojen ja lainsäädännön mukaisesti.</li>
          </ul>
        </>
      ),
    },

    {
      title: "Miten asiakkaiden palaute toimii?",
      content: (
        <>
          <p className="mb-2">
            Keikan jälkeen pyydämme asiakkaalta lyhyen palautteen: <strong>1–5 tähteä</strong> ja vapaa kommentti.
          </p>

          <ul className="list-disc pl-5 space-y-1">
            <li>Asiakkaalle lähtee automaattisesti sähköposti, jossa on suora linkki palautelomakkeeseen.</li>
            <li>Linkki aktivoituu <strong>esiintymispäivänä</strong>.</li>
            <li>Yksi palaute <strong>per varaus</strong>.</li>
            <li>Tähtiarvio kasvattaa <strong>julkista kokonaisarviotasi</strong> (keskiarvo).</li>
            <li>Saat kopion asiakkaan palautteesta sähköpostiisi (tähdet ja viesti).</li>
          </ul>
        </>
      )
    },

    {
      title: "Palkkion maksu ja välityspalkkio",
      content: (
        <>
          <p>
            Buukkeri ottaa <strong>välityspalkkion</strong> buukkauksen yhteydessä.
            Keikkapalkkion <strong>lopullinen summa</strong> sovitaan suoraan esiintyjän ja asiakkaan välillä.
          </p>

          <ul className="list-disc pl-5 mt-2 space-y-1">
            <li>
              Provisiotasot tällä hetkellä:
              <strong> bilebändit, duot &amp; DJ:t 15&nbsp;%</strong>,{" "}
              <strong>trubaduurit 12&nbsp;%</strong>.
            </li>
            <li>
              Provisio ja hyväksynnässä veloitettava <strong>varausmaksu</strong> lasketaan vain{" "}
              <strong>esiintymispalkkiosta</strong> — ei läpilaskutettavista kuluista
              (esim. km-kulut 0,59&nbsp;€/km tai lisäpalvelut).
            </li>
            <li>
              Varausmaksu <strong>hyvitetään</strong> loppusummasta, mutta sitä{" "}
              <strong>ei palauteta</strong> asiakkaan peruuttaessa (peruutusmaksuista
              vähennetään maksettu varausmaksu).
            </li>
            <li>
              Maksuliikenne voidaan halutessanne hoitaa myös <strong>Buukkerin kautta</strong>.
            </li>
          </ul>

          <p className="mt-2 text-sm opacity-80">
            Jos maksu kulkee Buukkerin kautta, Buukkeri laskuttaa asiakkaan ja tilittää esiintyjälle
            maksun vähennettynä välityspalkkiolla sekä perii erillisen{" "}
            <strong>laskutuslisän</strong> maksuliikenteen ja hallinnollisten kulujen kattamiseksi.
            Laskutuslisän suuruus ilmoitetaan aina etukäteen ja eritellään laskulla.
          </p>
        </>
      ),
    },

    {
      title: "Miten poistun palvelusta?",
      content: (
        <>
          <p>
            Tilin voi sulkea <strong>hallintapaneelista</strong>. Ennen sulkemista varmista nämä:
          </p>
          <ul className="list-disc pl-5 mt-2 space-y-1">
            <li>Poista <strong>esiintyjäkortti</strong> (jos niitä on useita, poista jokainen).</li>
            <li>Ei saa olla <strong>avoimia tarjouspyyntöjä</strong> eikä <strong>sovittuja keikkoja</strong>.</li>
          </ul>
          <p className="mt-2">
            Tiliä tai korttia ei voi poistaa, jos jokin pyyntö tai keikka on vielä kesken.
            Sovitut esiintymiset hoidetaan sovitusti — tai niihin etsitään korvaava esiintyjä — ennen tilin sulkemista.
          </p>
        </>
      ),
    },

    {
      title: "Voinko hallita useita esiintyjiä?",
      content: (
        <>
          Kyllä. Yhdellä tilillä on oletuksena yksi <strong>esiintyjäkortti</strong>.<br />
          Voit lisätä uusia kortteja hallintapaneelissa kohdasta “Lisää esiintyjä”.<br />
          Lisäkortit ovat maksullisia:{" "}
          <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-green-600/10 text-green-700 dark:text-green-400 border border-green-600/20 font-semibold">
            3,99&nbsp;€/kk
          </span>{" "}
          per kortti. Jokaisella kortilla on oma kalenteri, hinnat ja media-aineisto.
        </>
      ),
    },
  ];

  return (
    <>
      <Helmet>
        <title>Esiintyjien usein kysytyt – Buukkeri</title>
        <meta
          name="description"
          content="Usein kysytyt kysymykset Buukkerin esiintyjille: liittyminen, profiili, hinnat, kalenteri, palkkiot ja sopimuskäytännöt."
        />
        <script type="application/ld+json">
          {JSON.stringify(faqEsiintyjatJsonLd)}
        </script>
      </Helmet>

      {/* HERO */}
      <VideoHero
        mp4Src={ukkhero}
        poster={poster}
        className="h-[50vh] min-h-[260px] mt-20"
        overlayClass={`bg-gradient-to-b ${heroOverlay}`}
        respectReducedMotion={false}
      >
        <motion.h1
          className="font-customFont2 dark:text-shadow-lg text-center text-electric dark:text-primary text-5xl sm:text-6xl md:text-4xl lg:text-5xl xl:text-6xl 2xl:text-6xl font-bold leading-tight"
          initial={{ y: 12, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.5 }}
        >
          Esiintyjien UKK
        </motion.h1>
        <motion.p
          className="mt-3 dark:text-white/80 text-white/80 font-customFont1 text-lg text-center text-shadow-lg"
          initial={{ y: 12, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.5, delay: 0.05 }}
        >
          Kaikki olennainen Buukkeriin liittymisestä, profiilista, hinnoittelusta ja käytännöistä esiintyjille.
        </motion.p>
      </VideoHero>

      {/* Lista */}
      <main className="relative w-full pb-16">
        <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">
          <div className="mt-8 grid gap-3">
            {faqs.map((f, idx) => (
              <motion.div
                key={idx}
                initial={{ y: 10, opacity: 0 }}
                whileInView={{ y: 0, opacity: 1 }}
                viewport={{ once: true, margin: "-10% 0px -10% 0px" }}
                transition={{ duration: 0.3, ease: "easeOut", delay: idx * 0.03 }}
              >
                <FAQItem
                  i={idx}
                  title={f.title}
                  isOpen={openIndex === idx}
                  onToggle={() => toggle(idx)}
                >
                  {f.content}
                </FAQItem>
              </motion.div>
            ))}
          </div>
        </div>
      </main>
    </>
  );
}
